import React, { useState } from 'react';
import {
  Box,
  Typography,
  Link as MuiLink,
  Chip,
  alpha,
  useTheme,
  Collapse,
  IconButton,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TopicDiagram from './TopicDiagram';

const ACCENT = '#137274';
const PINK = '#f50057';

const toneMeta = {
  tip: { color: ACCENT, icon: LightbulbOutlinedIcon, label: 'Tip' },
  warn: { color: '#d97706', icon: WarningAmberOutlinedIcon, label: 'Watch out' },
  interview: { color: PINK, icon: RecordVoiceOverOutlinedIcon, label: 'Interview' },
};

const QaBlock = ({ items, accent, border, muted, surface, isDark }) => {
  const [openIdx, setOpenIdx] = useState(0);
  if (!items?.length) return null;

  return (
    <Box sx={{ my: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <RecordVoiceOverOutlinedIcon sx={{ fontSize: 20, color: PINK }} />
        <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', letterSpacing: '-0.02em' }}>
          Interview questions & answers
        </Typography>
        <Chip
          label={`${items.length} Q`}
          size="small"
          sx={{
            ml: 0.5,
            height: 22,
            fontWeight: 800,
            fontSize: '0.68rem',
            bgcolor: alpha(PINK, isDark ? 0.18 : 0.1),
            color: PINK,
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {items.map((item, idx) => {
          const open = openIdx === idx;
          return (
            <Box
              key={item.q || idx}
              sx={{
                borderRadius: 2,
                border: `1px solid ${open ? alpha(accent, 0.45) : border}`,
                bgcolor: open ? alpha(accent, isDark ? 0.1 : 0.04) : surface,
                overflow: 'hidden',
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => setOpenIdx(open ? -1 : idx)}
                sx={{
                  all: 'unset',
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.25,
                  width: '100%',
                  cursor: 'pointer',
                  px: 1.5,
                  py: 1.35,
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(PINK, isDark ? 0.22 : 0.12),
                    color: PINK,
                    fontWeight: 800,
                    fontSize: '0.72rem',
                    mt: 0.15,
                  }}
                >
                  {idx + 1}
                </Box>
                <Typography
                  sx={{
                    flex: 1,
                    fontWeight: 800,
                    fontSize: '0.98rem',
                    lineHeight: 1.5,
                    color: isDark ? '#e6edf3' : '#1a2330',
                    pt: 0.2,
                  }}
                >
                  {item.q}
                </Typography>
                <IconButton
                  size="small"
                  tabIndex={-1}
                  sx={{
                    color: muted,
                    transform: open ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.15s',
                  }}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
              </Box>
              <Collapse in={open}>
                <Box sx={{ px: 1.5, pb: 1.75, pl: 6.25 }}>
                  <Typography
                    sx={{
                      fontSize: '0.96rem',
                      lineHeight: 1.75,
                      color: isDark ? '#c9d1d9' : '#2d3740',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {item.a}
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const TopicArticle = ({ article, accent = ACCENT }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const surface = isDark ? '#0d1117' : '#f6f8fa';

  if (!article) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 800, mb: 1 }}>Article coming soon</Typography>
        <Typography sx={{ color: muted, fontSize: '0.9rem' }}>
          This topic is in the curriculum — content is being finalized.
        </Typography>
      </Box>
    );
  }

  const blocks = article.blocks || [];
  const hasQaBlock = blocks.some((b) => b.type === 'qa');
  const fallbackQuestions = !hasQaBlock && article.questions?.length ? article.questions : null;

  return (
    <Box sx={{ pb: 4 }}>
      <Chip
        label={`${article.readingMinutes || 8} min read`}
        size="small"
        sx={{
          mb: 1.5,
          fontWeight: 700,
          bgcolor: alpha(accent, isDark ? 0.2 : 0.1),
          color: accent,
        }}
      />
      <Typography
        sx={{
          fontWeight: 900,
          fontSize: { xs: '1.75rem', md: '2.25rem' },
          letterSpacing: '-0.035em',
          lineHeight: 1.18,
          mb: 1.25,
        }}
      >
        {article.title}
      </Typography>
      {article.subtitle && (
        <Typography sx={{ color: muted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.6, mb: 4, maxWidth: 760 }}>
          {article.subtitle}
        </Typography>
      )}

      {blocks.map((block, idx) => {
        const key = `${block.type}-${idx}`;

        if (block.type === 'p') {
          return (
            <Typography
              key={key}
              sx={{
                color: isDark ? '#c9d1d9' : '#2d3740',
                fontSize: { xs: '1.02rem', md: '1.09rem' },
                lineHeight: 1.8,
                mb: 2.25,
              }}
            >
              {block.text}
            </Typography>
          );
        }

        if (block.type === 'h') {
          return (
            <Typography
              key={key}
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.25rem', md: '1.4rem' },
                letterSpacing: '-0.02em',
                lineHeight: 1.35,
                mt: 4,
                mb: 1.5,
              }}
            >
              {block.text}
            </Typography>
          );
        }

        if (block.type === 'ul' || block.type === 'ol') {
          const ListTag = block.type === 'ol' ? 'ol' : 'ul';
          return (
            <Box
              key={key}
              component={ListTag}
              sx={{
                m: 0,
                mb: 2.5,
                pl: 3,
                color: isDark ? '#c9d1d9' : '#2d3740',
                '& li': { mb: 1.1, fontSize: { xs: '1rem', md: '1.06rem' }, lineHeight: 1.75 },
              }}
            >
              {(block.items || []).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </Box>
          );
        }

        if (block.type === 'callout') {
          const meta = toneMeta[block.tone] || toneMeta.tip;
          const Icon = meta.icon;
          return (
            <Box
              key={key}
              sx={{
                my: 2,
                p: 1.75,
                borderRadius: 2,
                border: `1px solid ${alpha(meta.color, 0.35)}`,
                bgcolor: alpha(meta.color, isDark ? 0.12 : 0.07),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, color: meta.color }}>
                <Icon sx={{ fontSize: 19 }} />
                <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }}>
                  {block.title || meta.label}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: { xs: '0.98rem', md: '1.02rem' }, lineHeight: 1.7, color: isDark ? '#c9d1d9' : '#334' }}>
                {block.text}
              </Typography>
            </Box>
          );
        }

        if (block.type === 'formula') {
          return (
            <Box
              key={key}
              sx={{
                my: 2,
                p: 2,
                borderRadius: 2,
                border: `1px dashed ${border}`,
                bgcolor: surface,
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontWeight: 700,
                  fontSize: { xs: '0.98rem', sm: '1.12rem' },
                  letterSpacing: '0.01em',
                  mb: block.meaning ? 1.25 : 0,
                }}
              >
                {block.latex}
              </Typography>
              {block.meaning && (
                <Typography sx={{ color: muted, fontSize: '0.92rem', lineHeight: 1.6 }}>
                  {block.meaning}
                </Typography>
              )}
            </Box>
          );
        }

        if (block.type === 'code') {
          return (
            <Box key={key} sx={{ my: 2 }}>
              {(block.title || block.language) && (
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 800,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: muted,
                    mb: 0.75,
                  }}
                >
                  {block.title || block.language}
                </Typography>
              )}
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.75,
                  borderRadius: 2,
                  border: `1px solid ${border}`,
                  bgcolor: isDark ? '#010409' : '#0d1117',
                  color: '#e6edf3',
                  overflow: 'auto',
                  fontSize: '0.88rem',
                  lineHeight: 1.65,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                }}
              >
                <code>{block.code}</code>
              </Box>
            </Box>
          );
        }

        if (block.type === 'diagram') {
          return <TopicDiagram key={key} kind={block.kind} caption={block.caption} />;
        }

        if (block.type === 'table') {
          return (
            <Box
              key={key}
              sx={{
                my: 2,
                overflowX: 'auto',
                borderRadius: 2,
                border: `1px solid ${border}`,
              }}
            >
              <Box
                component="table"
                sx={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.94rem',
                  '& th, & td': {
                    px: 1.75,
                    py: 1.3,
                    textAlign: 'left',
                    borderBottom: `1px solid ${border}`,
                    verticalAlign: 'top',
                    lineHeight: 1.55,
                  },
                  '& th': {
                    bgcolor: isDark ? alpha('#fff', 0.04) : alpha(ACCENT, 0.06),
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                  },
                  '& tr:last-child td': { borderBottom: 'none' },
                }}
              >
                <thead>
                  <tr>
                    {(block.headers || []).map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(block.rows || []).map((row, rIdx) => (
                    <tr key={rIdx}>
                      {row.map((cell, cIdx) => (
                        <td key={`${rIdx}-${cIdx}`}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
          );
        }

        if (block.type === 'steps') {
          return (
            <Box key={key} sx={{ my: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {(block.items || []).map((step, sIdx) => (
                <Box
                  key={step.title || sIdx}
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${border}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(accent, isDark ? 0.25 : 0.12),
                      color: accent,
                      fontWeight: 800,
                      fontSize: '0.75rem',
                    }}
                  >
                    {sIdx + 1}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.98rem', mb: 0.4 }}>
                      {step.title}
                    </Typography>
                    <Typography sx={{ color: muted, fontSize: '0.94rem', lineHeight: 1.65 }}>
                      {step.text}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          );
        }

        if (block.type === 'qa') {
          return (
            <QaBlock
              key={key}
              items={block.items}
              accent={accent}
              border={border}
              muted={muted}
              surface={surface}
              isDark={isDark}
            />
          );
        }

        return null;
      })}

      {fallbackQuestions && (
        <QaBlock
          items={fallbackQuestions}
          accent={accent}
          border={border}
          muted={muted}
          surface={surface}
          isDark={isDark}
        />
      )}

      {article.references?.length > 0 && (
        <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${border}` }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', mb: 1.5 }}>
            Go deeper
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {article.references.map((ref) => (
              <MuiLink
                key={ref.url}
                href={ref.url}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  color: accent,
                  fontWeight: 700,
                  fontSize: '0.96rem',
                  width: 'fit-content',
                }}
              >
                {ref.label}
                <OpenInNewIcon sx={{ fontSize: 15 }} />
              </MuiLink>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TopicArticle;
